import React, { useContext, ReactNode, useMemo, useEffect, useRef, useState, FC } from 'react';
import { Store } from 'react-notifications-component';
import warnSfx from 'audio/alarm.mp3';
import { AddErrorToLog } from 'components/ErrorHistoryOverview';
import { isWatchDevice } from 'component_utils/utils';
import classNames from 'classnames';
import { sample } from 'lodash';

declare global {
  interface Window {
    be_notifications: {
      add_notification: TAddNotification
    };
  }
}

type ColorTypes = 'success' | 'danger' | 'info' | 'default' | 'warning';

type Options = {
  centered?: boolean
  persistent?: boolean
}
export type TAddNotification = (type: ColorTypes, title: ReactNode, msg?: ReactNode, options?: Options) => void;
export interface INotificationContext {
  add: TAddNotification;
  setDuration: (duration: number) => void;
}

const NotificationContext = React.createContext<INotificationContext>({} as INotificationContext);

export const Tests = {
  NotificationContext,
};

export function useNotifications(): INotificationContext {
  return useContext(NotificationContext);
}

let addScreenFillingNotification = (notification: any) => {}
let duration = 5000;
export const setNotificationDuration = (d: number) => (duration = d);
export const addNotification: TAddNotification = (type, title, msg?, options?) => {
  if (isWatchDevice()) {
    addScreenFillingNotification({
      type, title, msg
    })
    return
  }

  Store.addNotification({
    title: (
      <Notification type={type} title={title} msg={msg}>
        {title}
      </Notification>
    ),
    message: msg || <div />,
    type: type,
    insert: 'top',
    container: options?.centered ? 'center' : 'top-right',
    animationIn: ['animated', 'fadeInRight'],
    animationOut: ['animated', 'fadeOutRight'],
    dismiss: options?.persistent ? null : {
      duration,
      pauseOnHover: true,
      onScreen: true,
    },
  });
};

const getUserConfiguredErrorSound = () => {
  const s = window?.be_app_config?.settings?.alternativeErrorPopupSoundUrl?.trim()
  if (!s) return null
  
  const entries = s.split('\n').map(it => it.trim()).filter(it => it)
  const entry = sample(entries)
  return entry
}

const Notification: React.FC<any> = ({ children, type, title, msg }) => {
  const titleRef = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const container = titleRef.current.parentElement.parentElement;
    const titleText = (container.querySelector('.rnc__notification-title') as any)?.innerText || '';
    const msgText = (container.querySelector('.rnc__notification-message') as any)?.innerText || '';
    const appName = document.getElementById('current-app-name')?.innerText || '';
    AddErrorToLog(appName, type, title, titleText, msg, msgText);

    // play a noise
    if (type === 'danger') {
      const audio = new Audio(getUserConfiguredErrorSound() || warnSfx);
      audio.play();
    }
  }, [type, title, msg]);

  return <span ref={titleRef}>{children}</span>;
};

const ScreenFillingNotification: FC<{ 
  notification: any;
  onRemove: any;
}> = ({ notification: { type, title, msg }, onRemove }) => {
  const [animationName, setAnimationName] = useState('fadeIn')
  const [timerState, setTimerState] = useState('running')

  useEffect(() => {
    if (type === 'danger') {
      const audio = new Audio(getUserConfiguredErrorSound() || warnSfx);
      audio.play();
    }
  }, [type])
  return (
    <div 
      className={classNames('bg-'+type, 'p-5', 'animated', animationName)} 
        style={{
        position: 'absolute',
        inset: '0px',
        zIndex: 2147483647,
        overflowY: 'auto',
        animationDuration: '0.25s'
      }} 
      onClick={() => setAnimationName('fadeOut')} 
      onContextMenu={e => {
        e.preventDefault()
        setTimerState('paused')
      }}
      onAnimationEnd={() => {
        if (animationName === 'fadeOut') {
          onRemove()
        }
      }}
    >
      <div className='h-100 w-100' style={{ overflowY: 'auto' }}>
        <div className="rnc__notification-timer">
          <div
            className="rnc__notification-timer-filler"
            onAnimationEnd={() => setAnimationName('fadeOut')}
            style={{
              animationName: 'timer',
              animationDuration: `${duration}ms`,
              animationTimingFunction: 'linear',
              animationFillMode: 'forwards',
              animationDelay: `0`,
              animationPlayState: timerState,
              backgroundColor: '#fff'
            }}
          ></div>
        </div>
        <h1>{title}</h1>
        {msg}
      </div>
    </div>
  )
}

export const NotificationManager: React.FC = ({ children }) => {
  const [screenFillingNotifications, setScreenFillingNotifications] = useState<any[]>([])
  addScreenFillingNotification = (notification) => setScreenFillingNotifications(old => [...old, notification])
  const ctx = useMemo(() => {
    return {
      add: addNotification,
      setDuration: setNotificationDuration,
    };
  }, []);

  window.be_notifications = {
    add_notification: addNotification
  }

  const currentNotification = screenFillingNotifications[0]
  return (
    <NotificationContext.Provider value={ctx}>
      {children}
      {currentNotification && <ScreenFillingNotification 
        notification={currentNotification}
        onRemove={() => setScreenFillingNotifications(old => old.slice(1))}
      />}
    </NotificationContext.Provider>
  );
};
